/* Free Shipping Nudge — <free-shipping-nudge>
 * In-cart progress bar toward the free-shipping threshold + suggestions that
 * close the gap. Re-renders on every cart-update (same pub/sub as the promo
 * widget). Doc: docs/free-shipping-nudge.md
 *
 * Suggestion sources:
 *   another  — +1 of a per-side part already in the cart (bearings wear in pairs)
 *   parts    — parts from the SAME VEHICLE COLLECTION as the priciest cart line
 *              (rendered by the section's Liquid; re-fetched via the Section
 *              Rendering API when the cart's product set changes). Filtered by a
 *              part taxonomy: never the same part, never a part the customer's
 *              kit already contains, never a kit that contains what they have.
 *              Shopify recommendations are only a fitment-filtered fallback.
 *   apparel  — in-house apparel/supply from the configured collection
 * Products in cfg.excludeCount never count toward the threshold (Printful items
 * ship on their own delivery profiles) and are never suggested.
 */
(() => {
  if (customElements.get('free-shipping-nudge')) return;

  const routes = () =>
    window.routes || { cart_url: '/cart', cart_add_url: '/cart/add', cart_change_url: '/cart/change' };
  const SOURCE = 'free-shipping-nudge';
  const SECTION_ID = 'free-shipping-nudge';
  const money = (cents) =>
    '$' + (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const escapeHtml = (s) =>
    String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const imgSrc = (src) => (src && src.startsWith('//') ? 'https:' + src : src || '');

  /* ---------- part taxonomy ---------- */

  // Order matters: more specific patterns first (a "Shock Extension Kit" is not a shock,
  // a "Ball Joint Removal Tool" is a tool, "Leveling System Stage 1" is a system not keys).
  const PART_CLASSES = [
    ['tool', /\btools?\b|socket|pliers|grease|puller|removal|\bwrench\b/],
    ['axle-bolts', /flange bolt/],
    ['bearing-hardware', /o-ring|dust cap|spindle bolt|spindle nut|\baxle nut\b|hub bolt/],
    ['speed-sensor', /speed sensor|abs sensor/],
    ['leveling-system', /leveling system|leveling kit|\bstage \d|lift kit|lift system/],
    ['coil-spring', /coil spring|leveling spacer|strut spacer|coil spacer|spring spacer/],
    ['leveling-keys', /leveling key|torsion key/],
    ['shock-extension', /shock extension|shock spacer/],
    ['bump-stop', /bump stop/],
    ['shock', /\bshocks?\b|coilover|strut\b/],
    ['uca-bushing', /control arm bushing|bushing/],
    ['uca', /control arm|\buca\b/],
    ['ball-joint', /ball joint/],
    ['bearing', /wheel bearing|hub assembly|wheel hub/],
    ['cv-axle', /cv axle|axle assembly|\baxle\b/],
    ['sway-bar', /sway bar|end link/],
    ['track-bar', /track bar|panhard/],
    ['alignment', /alignment|cam bolt|cam pin|caster|camber/],
    ['steering', /tie rod|steering|drag link|pitman|idler/],
    ['plate', /logo plate|show plate|license plate/],
  ];

  const TYPE_CLASSES = {
    Shocks: 'shock',
    Bearing: 'bearing',
    'Ball Joint': 'ball-joint',
    Axle: 'cv-axle',
    'Sway Bar': 'sway-bar',
    Alignment: 'alignment',
    'Track Bar': 'track-bar',
    Bushings: 'uca-bushing',
    Tools: 'tool',
    Steering: 'steering',
    'Bump Stops': 'bump-stop',
    'Shock Spacer': 'shock-extension',
    'Leveling Kit': 'leveling-system',
    Suspension: 'suspension',
    'Logo Plate': 'plate',
  };

  // Classes that mean "the same thing twice" is fine (two different tools are not duplicates).
  const NON_EXCLUSIVE = new Set(['tool', 'other', 'plate', 'supply', 'suspension']);

  // What a kit already contains — never suggest these to someone who has the kit,
  // and never suggest the kit to someone who already has one of these.
  function contentsOf(cls, title) {
    const t = String(title || '').toLowerCase();
    if (cls === 'leveling-system') {
      const inner = ['leveling-keys', 'shock-extension', 'coil-spring', 'bump-stop', 'leveling-system'];
      if (/stage [23]|control arm|\buca\b/.test(t)) inner.push('uca', 'uca-bushing', 'ball-joint');
      if (/stage 3|\bfox\b|shock/.test(t)) inner.push('shock');
      return inner;
    }
    if (cls === 'uca') return ['uca-bushing', 'ball-joint'];
    if (cls === 'bearing') return ['bearing-hardware'];
    return [];
  }

  // Complements, best first, per anchor class. Anything not listed ranks after these.
  const PRIORITY = {
    'leveling-keys': ['shock-extension', 'alignment', 'shock', 'sway-bar', 'uca-bushing', 'ball-joint', 'bearing', 'bump-stop', 'tool'],
    'shock-extension': ['leveling-keys', 'alignment', 'shock', 'sway-bar', 'uca-bushing', 'ball-joint', 'bearing', 'tool'],
    'leveling-system': ['alignment', 'sway-bar', 'ball-joint', 'bearing', 'uca-bushing', 'shock', 'tool'],
    'coil-spring': ['shock-extension', 'shock', 'alignment', 'sway-bar', 'ball-joint', 'bearing', 'track-bar'],
    shock: ['shock-extension', 'leveling-keys', 'sway-bar', 'bump-stop', 'uca', 'ball-joint', 'bearing'],
    bearing: ['speed-sensor', 'ball-joint', 'cv-axle', 'bearing-hardware', 'axle-bolts', 'tool', 'uca-bushing', 'sway-bar', 'shock'],
    'speed-sensor': ['bearing', 'ball-joint', 'cv-axle', 'bearing-hardware', 'tool'],
    'ball-joint': ['bearing', 'uca-bushing', 'uca', 'alignment', 'tool', 'cv-axle', 'sway-bar'],
    'cv-axle': ['axle-bolts', 'bearing', 'ball-joint', 'tool', 'speed-sensor'],
    uca: ['alignment', 'shock', 'leveling-keys', 'sway-bar', 'bearing'],
    'uca-bushing': ['ball-joint', 'alignment', 'uca', 'sway-bar', 'bearing'],
    'sway-bar': ['leveling-keys', 'shock', 'shock-extension', 'uca-bushing', 'ball-joint', 'alignment'],
    alignment: ['leveling-keys', 'uca-bushing', 'ball-joint', 'sway-bar', 'bearing'],
    'track-bar': ['sway-bar', 'shock', 'steering', 'ball-joint'],
    steering: ['track-bar', 'ball-joint', 'alignment', 'bearing'],
  };

  function classify(title, type) {
    const t = String(title || '').toLowerCase();
    for (const [cls, re] of PART_CLASSES) if (re.test(t)) return cls;
    if (type && TYPE_CLASSES[type]) return TYPE_CLASSES[type];
    return 'other';
  }

  // Make / model / generation words that identify a vehicle in this store's titles
  // (only used for the recommendations FALLBACK — the vehicle pool is fitment-exact).
  const VEHICLE_VOCAB = new Set(
    (
      'jeep wrangler gladiator cherokee jk jl jt tj yj xj ' +
      'ram dodge ford f150 f-150 f250 f-250 f350 f-350 f450 f-450 f550 f-550 superduty excursion raptor bronco ranger ' +
      'chevy chevrolet gmc gm silverado sierra suburban tahoe yukon 1500 2500 3500 hd hummer h2 ' +
      'polaris rzr general xpedition can-am canam cam-am maverick x3 kawasaki teryx krx ' +
      'toyota tacoma tundra 4runner sequoia fj cruiser lexus gx470 gx460'
    ).split(' ')
  );

  class FreeShippingNudge extends HTMLElement {
    connectedCallback() {
      try {
        this.cfg = JSON.parse(this.querySelector('script[type="application/json"]').textContent);
      } catch (e) {
        return;
      }
      if (!this.cfg || !this.cfg.enabled || !(this.cfg.threshold > 0)) return;

      this.excludeCount = new Set((this.cfg.excludeCount || []).map((h) => h.trim()).filter(Boolean));
      this.excludeSuggest = new Set((this.cfg.excludeSuggest || []).map((h) => h.trim()).filter(Boolean));
      this.recCache = new Map();
      this.productCache = new Map();
      this.poolCache = new Map();
      if (this.cfg.cartSig !== undefined) {
        this.poolCache.set(this.cfg.cartSig, {
          pool: this.cfg.pool || [],
          vehicle: this.cfg.vehicle || [],
          anchorProductId: this.cfg.anchorProductId || 0,
        });
      }
      this.busy = false;
      // When true, this panel owns ALL cart perk messaging (free shipping + free tee) and
      // promo-auto-add.js skips its own notices — one integrated block instead of two.
      this.integrated = this.cfg.integratePromo !== false;

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event && event.source === SOURCE) return;
          this.scheduleCheck();
        });
      }
      // The drawer's inner markup is replaced on every cart mutation — watch for it
      // and re-pin the panel right under the header so it can't get lost or reordered.
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof MutationObserver !== 'undefined') {
        this.observer = new MutationObserver(() => this.pin());
        this.observer.observe(drawer, { childList: true, subtree: true });
      }
      this.scheduleCheck();
    }

    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
      if (this.observer) this.observer.disconnect();
    }

    scheduleCheck() {
      clearTimeout(this.checkTimer);
      this.checkTimer = setTimeout(() => this.check(), 350);
    }

    /* ---------- cart math ---------- */

    countsToward(item) {
      return !this.excludeCount.has(item.handle);
    }

    qualifyingSubtotal(cart) {
      return cart.items.reduce((sum, i) => (this.countsToward(i) ? sum + (i.final_line_price || 0) : sum), 0);
    }

    isSuggestable(item) {
      if (!this.countsToward(item)) return false;
      if (this.excludeSuggest.has(item.handle)) return false;
      const p = item.properties || {};
      if (p._auto_added || p._pcref) return false; // promo tee / powdercoat service pairing
      if (item.final_price <= 0) return false;
      return true;
    }

    isBearing(item) {
      const t = `${item.product_type || ''} ${item.product_title || item.title || ''}`.toLowerCase();
      return /bearing|hub assembly|wheel hub/.test(t) && !/sensor|o-ring|dust cap|spindle/.test(t);
    }

    // Parts sold one-per-side (a second unit is a genuine upsell, not a duplicate).
    isPerSide(item) {
      if (this.isBearing(item)) return true;
      const cls = classify(item.product_title || item.title, item.product_type);
      return cls === 'ball-joint' || cls === 'cv-axle' || (cls === 'shock' && /single/i.test(item.product_title || ''));
    }

    isApparel(item) {
      const t = `${item.product_type || ''}`.toLowerCase();
      if (/shirt|tee|hat|hoodie|supply|embroidery|logo plate/.test(t)) return true;
      return (this.cfg.apparel || []).some((p) => p.id === item.product_id);
    }

    cartSignature(cart) {
      return cart.items
        .map((i) => i.product_id)
        .sort((a, b) => a - b)
        .join(',');
    }

    async check() {
      if (this.busy) return;
      this.busy = true;
      try {
        const cart = await fetch(`${routes().cart_url}.js`).then((r) => r.json());
        this.cart = cart;
        if (!cart || cart.item_count === 0) {
          this.removePanel();
          return;
        }
        const state = this.perkState(cart);
        if (!state.next) {
          this.render({ ...state, suggestions: [] });
          return;
        }
        // Render the bar immediately; suggestions fill in when their fetches resolve.
        this.render({ ...state, suggestions: null });
        const suggestions = await this.buildSuggestions(cart, state.gap, state.perkPhrase);
        // Cart may have changed while we were fetching — only paint if still current.
        if (this.cart === cart) this.render({ ...state, suggestions });
      } catch (e) {
        console.warn('[free-shipping-nudge]', e);
      } finally {
        this.busy = false;
      }
    }

    /* ---------- perks: free shipping + (optionally) the free-tee promo ---------- */

    // Copy with tokens. A setting that lost a required token falls back to the default.
    t(key, vars) {
      const raw = this.cfg.copy && this.cfg.copy[key];
      const need = Object.keys(vars || {});
      const usable = raw && need.every((k) => raw.includes(`[${k}]`) || k === 'ship' || k === 'tee');
      let s = usable ? raw : COPY_DEFAULTS[key] || '';
      Object.entries(vars || {}).forEach(([k, v]) => {
        s = s.split(`[${k}]`).join(v);
      });
      return s;
    }

    // Reads the live promo-auto-add widget (same page) so the tee milestone mirrors its
    // real rules: enabled, in date window, not in cooldown, threshold, tee line in cart.
    promoState(cart) {
      if (!this.integrated) return null;
      const el = document.querySelector('promo-auto-add');
      if (!el) return null;
      let pc = el.cfg;
      if (!pc) {
        try {
          pc = JSON.parse(el.querySelector('script[type="application/json"]').textContent);
        } catch (e) {
          return null;
        }
      }
      if (!pc || !pc.enabled || !(pc.minSubtotal > 0) || pc.available === false) return null;
      if (typeof el.inDateWindow === 'function' && !el.inDateWindow()) return null;
      if (typeof el.inCooldown === 'function' && el.inCooldown()) return null;
      const line = cart.items.find((i) => (pc.productId ? i.product_id === pc.productId : i.id === pc.variantId));
      // Same math as the promo widget: everything except the tee itself.
      const qualifying = cart.items.reduce((s, i) => (i === line ? s : s + (i.final_line_price || 0)), 0);
      return {
        threshold: pc.minSubtotal,
        qualifying,
        teeLine: line || null,
        image: pc.imageUrl || '',
        productId: pc.productId,
      };
    }

    perkState(cart) {
      const cfg = this.cfg;
      const shipQ = this.qualifyingSubtotal(cart);
      const ship = { key: 'ship', threshold: cfg.threshold, qualifying: shipQ, reached: shipQ >= cfg.threshold };
      const promo = this.promoState(cart);
      const tee = promo
        ? { key: 'tee', threshold: promo.threshold, qualifying: promo.qualifying, reached: promo.qualifying >= promo.threshold, line: promo.teeLine, image: promo.image }
        : null;
      const shipLabel = this.t('shipLabel');
      const teeLabel = this.t('teeLabel');

      let next = null;
      if (!ship.reached) next = ship;
      else if (tee && !tee.reached) next = tee;
      const gap = next ? next.threshold - next.qualifying : 0;
      const perkPhrase = next && next.key === 'tee' ? `your ${teeLabel}` : shipLabel;

      // Headline + sub line
      let headline;
      let sub = '';
      let unlocked = false;
      if (next && next.key === 'ship') {
        headline = this.t('progress', { amount: money(gap), perk: shipLabel });
        if (tee && tee.line && !tee.reached) {
          sub = this.t('teeWarn', { tee: teeLabel, amount: money(tee.threshold - tee.qualifying) });
        } else if (tee) {
          sub = this.t('next', { amount: money(Math.max(0, tee.threshold - tee.qualifying - gap)), tee: teeLabel });
        }
      } else if (next && next.key === 'tee') {
        headline = tee.line
          ? this.t('teeWarn', { tee: teeLabel, amount: money(gap) })
          : this.t('progress', { amount: money(gap), perk: `a ${teeLabel}` });
        sub = this.t('unlocked', { perk: shipLabel });
      } else {
        unlocked = true;
        if (tee && tee.line) headline = this.t('bothUnlocked', { ship: shipLabel, tee: teeLabel });
        else if (tee) {
          headline = this.t('unlocked', { perk: shipLabel });
          sub = this.t('teeReady', { tee: teeLabel });
        } else headline = this.t('unlocked', { perk: shipLabel });
      }

      // Track = one segment per milestone. Each segment fills with ITS milestone's color in
      // proportion to progress from the previous milestone to that one.
      const max = tee ? Math.max(tee.threshold, ship.threshold) : ship.threshold;
      const clamp = (v) => Math.max(0, Math.min(100, Math.round(v * 100)));
      const shipAt = tee ? Math.round((ship.threshold / max) * 100) : 100;
      const milestones = [
        {
          key: 'ship',
          at: shipAt,
          from: 0,
          fill: clamp(shipQ / ship.threshold),
          label: this.t('shipShort'),
          price: '$' + Math.round(ship.threshold / 100),
          reached: ship.reached,
        },
      ];
      if (tee) {
        milestones.push({
          key: 'tee',
          at: 100,
          from: shipAt,
          fill: ship.reached ? clamp((tee.qualifying - ship.threshold) / Math.max(1, tee.threshold - ship.threshold)) : 0,
          label: this.t('teeShort'),
          price: '$' + Math.round(tee.threshold / 100),
          reached: tee.reached && !!tee.line,
          inCart: !!tee.line,
        });
      }
      const pct = clamp(shipQ / max);

      return { ship, tee, next, gap, perkPhrase, headline, sub, unlocked, pct, milestones };
    }

    /* ---------- vehicle pool (Section Rendering API) ---------- */

    async vehiclePool(cart) {
      const sig = this.cartSignature(cart);
      if (this.poolCache.has(sig)) return this.poolCache.get(sig);
      let entry = { pool: [], vehicle: [], anchorProductId: 0 };
      try {
        const res = await fetch(`/?section_id=${SECTION_ID}`, { credentials: 'same-origin' });
        if (res.ok) {
          const html = await res.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const node = doc.querySelector('free-shipping-nudge script[type="application/json"]');
          if (node) {
            const fresh = JSON.parse(node.textContent);
            entry = { pool: fresh.pool || [], vehicle: fresh.vehicle || [], anchorProductId: fresh.anchorProductId || 0 };
            // The server saw the same cart → trust it for this signature.
            if (fresh.cartSig !== undefined && fresh.cartSig !== sig) entry.stale = true;
          }
        }
      } catch (e) {
        console.warn('[free-shipping-nudge] pool', e);
      }
      if (!entry.stale) this.poolCache.set(sig, entry);
      return entry;
    }

    /* ---------- suggestions ---------- */

    async buildSuggestions(cart, gap, perk) {
      const cfg = this.cfg;
      const inCart = new Set(cart.items.map((i) => i.product_id));
      const max = Math.max(1, cfg.maxSuggestions || 5);
      const small = gap <= cfg.smallGap;
      this._perk = perk || this.t('shipLabel');

      const another = cfg.suggestAnother ? this.anotherOf(cart, gap) : [];
      const apparel = cfg.suggestApparel ? this.apparelFor(gap, inCart) : [];
      let parts = { closers: [], partials: [] };
      if (cfg.suggestRelated) {
        try {
          parts = await this.partsFor(cart, gap, inCart);
        } catch (e) {
          console.warn('[free-shipping-nudge] parts', e);
        }
      }

      // Small gap: a tee is the natural closer, then parts. Big gap: parts lead.
      const ordered = small
        ? [apparel[0], ...another, ...parts.closers, ...parts.partials, ...apparel.slice(1)]
        : [...another, ...parts.closers, ...parts.partials, ...apparel];

      const out = [];
      const seen = new Set();
      for (const s of ordered) {
        if (!s || seen.has(s.dedupe)) continue;
        seen.add(s.dedupe);
        out.push(s);
        if (out.length >= max) break;
      }
      return out;
    }

    // +1 of an existing line when one more unit clears the gap. Only for parts sold
    // per side — a second leveling kit or tool is a duplicate, not an upsell.
    anotherOf(cart, gap) {
      return cart.items
        .filter((i) => this.isSuggestable(i) && this.isPerSide(i) && i.final_price >= gap)
        .map((i) => ({
          kind: 'another',
          dedupe: `p${i.product_id}`,
          lineKey: i.key,
          quantity: i.quantity,
          variantId: i.id,
          title: i.product_title || i.title,
          subtitle: i.variant_title && i.variant_title !== 'Default Title' ? i.variant_title : '',
          price: i.final_price,
          image: i.image,
          url: i.url,
          reason: this.isBearing(i) ? this.t('pair', { perk: this._perk }) : this.t('another', { perk: this._perk }),
          inCart: true,
          overshoot: i.final_price - gap,
        }))
        .sort((a, b) => a.overshoot - b.overshoot);
    }

    apparelFor(gap, inCart) {
      return (this.cfg.apparel || [])
        .filter((p) => !inCart.has(p.id) && p.price >= gap && p.firstVariant)
        .map((p) => ({
          kind: 'apparel',
          dedupe: `p${p.id}`,
          productId: p.id,
          handle: p.handle,
          variantId: p.firstVariant,
          variantCount: p.variantCount,
          title: p.title,
          subtitle: '',
          price: p.price,
          image: p.image,
          url: p.url,
          reason: this.t('apparel', { perk: this._perk }),
          overshoot: p.price - gap,
        }))
        .sort((a, b) => a.overshoot - b.overshoot)
        .slice(0, 3);
    }

    // Fitment-exact parts from the anchor's vehicle collection(s); recommendations only
    // when the cart has no vehicle part at all.
    async partsFor(cart, gap, inCart) {
      const lines = cart.items.filter((i) => this.isSuggestable(i) && !this.isApparel(i));
      if (!lines.length) return { closers: [], partials: [] };

      const entry = await this.vehiclePool(cart);
      let anchor = entry.anchorProductId ? lines.find((i) => i.product_id === entry.anchorProductId) : null;
      if (!anchor) anchor = lines.slice().sort((a, b) => b.final_line_price - a.final_line_price)[0];
      const anchorTitle = anchor.product_title || anchor.title;
      const anchorCls = classify(anchorTitle, anchor.product_type);

      // Everything the cart already covers: each line's class + what its kits contain.
      const cartClasses = new Set();
      const cartContained = new Set();
      lines.forEach((i) => {
        const cls = classify(i.product_title || i.title, i.product_type);
        cartClasses.add(cls);
        contentsOf(cls, i.product_title || i.title).forEach((c) => cartContained.add(c));
      });

      let candidates;
      let fitmentExact = true;
      if (entry.pool && entry.pool.length) {
        candidates = entry.pool;
      } else {
        fitmentExact = false;
        candidates = await this.recommendationsFor(anchor);
      }

      const cap = Math.max(gap * 3, 6000);
      const anchorTokens = this.vehicleTokens(anchorTitle);
      const cartFamilies = new Set(lines.map((i) => this.familyKey(i.product_title || i.title)));
      const priority = PRIORITY[anchorCls] || [];

      const scored = candidates
        .filter((p) => p && p.available !== false && !inCart.has(p.id))
        .filter((p) => !this.excludeCount.has(p.handle) && !this.excludeSuggest.has(p.handle))
        .filter((p) => this.yearsCompatible(anchorTitle, p.title))
        .filter((p) => fitmentExact || (this.fitsAnchor(anchorTokens, this.vehicleTokens(p.title)) && !cartFamilies.has(this.familyKey(p.title))))
        .map((p) => ({ p, cls: classify(p.title, p.type) }))
        .filter(({ p, cls }) => {
          if (cls === 'plate' || cls === 'supply') return false; // apparel handles those
          if (cartClasses.has(cls) && !NON_EXCLUSIVE.has(cls)) return false; // same part, different year/brand
          if (cartContained.has(cls)) return false; // their kit already includes it
          const inner = contentsOf(cls, p.title);
          if (inner.some((c) => cartClasses.has(c))) return false; // a kit that includes what they have
          return true;
        })
        .map(({ p, cls }) => {
          const idx = priority.indexOf(cls);
          const rank = idx === -1 ? (cls === 'tool' || cls === 'other' ? 200 : 100) : idx;
          return { p, cls, rank };
        });

      const toSuggestion = (p, closes) => ({
        kind: 'related',
        dedupe: `p${p.id}`,
        productId: p.id,
        handle: p.handle,
        variantId: p.firstVariant || (p.variants && (p.variants.find((v) => v.available) || p.variants[0]) || {}).id,
        variantCount: p.variantCount != null ? p.variantCount : (p.variants || []).length,
        title: p.title,
        subtitle: '',
        price: p.price,
        image: p.image || p.featured_image,
        url: p.url,
        reason: closes
          ? this.t('related', { perk: this._perk })
          : this.t('partial', { amount: money(gap - p.price), perk: this._perk }),
        closes,
      });

      // One ranked list: best complement first; at equal rank a closer beats a partial,
      // then the smallest overshoot / biggest progress. Max 2 per class so five sway-bar
      // link lengths can't crowd out everything else.
      const minPartial = Math.max(gap * 0.15, 1000);
      const perClass = new Map();
      const ranked = scored
        .map((x) => ({ ...x, closes: x.p.price >= gap && x.p.price <= cap }))
        .filter((x) => x.closes || (x.p.price < gap && x.p.price >= minPartial))
        .sort(
          (a, b) =>
            a.rank - b.rank ||
            Number(b.closes) - Number(a.closes) ||
            (a.closes ? a.p.price - b.p.price : b.p.price - a.p.price)
        )
        .filter((x) => {
          const n = perClass.get(x.cls) || 0;
          if (n >= 2) return false;
          perClass.set(x.cls, n + 1);
          return true;
        })
        .map((x) => toSuggestion(x.p, x.closes))
        .filter((s) => s.variantId);

      return { closers: ranked, partials: [] };
    }

    async recommendationsFor(anchor) {
      const base = this.cfg.recommendationsUrl;
      if (!base) return [];
      let products = this.recCache.get(anchor.product_id);
      if (!products) {
        const res = await fetch(`${base}.json?product_id=${anchor.product_id}&limit=10&intent=related`, {
          credentials: 'same-origin',
        });
        if (!res.ok) return [];
        products = ((await res.json()).products || []).filter(Boolean);
        this.recCache.set(anchor.product_id, products);
      }
      return products;
    }

    /* ---------- title heuristics (recommendations fallback + belt-and-braces) ---------- */

    vehicleTokens(title) {
      const words = String(title || '')
        .toLowerCase()
        .replace(/[^a-z0-9\- ]/g, ' ')
        .split(/\s+/);
      return new Set(words.filter((w) => VEHICLE_VOCAB.has(w)));
    }

    // One token set must contain the other ("GM HD 2500 / 3500" ⊂ "… Silverado / Sierra" fits,
    // "Jeep Wrangler TJ" vs "Jeep Wrangler JK" doesn't). Vehicle-agnostic anchors only
    // get vehicle-agnostic candidates.
    fitsAnchor(anchorTokens, candidateTokens) {
      if (anchorTokens.size === 0) return candidateTokens.size === 0;
      if (candidateTokens.size === 0) return false;
      const a = [...anchorTokens];
      const c = [...candidateTokens];
      return a.every((t) => candidateTokens.has(t)) || c.every((t) => anchorTokens.has(t));
    }

    familyKey(title) {
      return String(title || '')
        .toLowerCase()
        .replace(/\b(19|20)\d{2}\b/g, ' ')
        .replace(/[^a-z0-9\- ]/g, ' ')
        .split(/\s+/)
        .filter((w) => w && !VEHICLE_VOCAB.has(w) && !NOISE_WORDS.has(w))
        .slice(0, 4)
        .join(' ');
    }

    // Model-year range in a title: "2011-2019", "1999 - 2006", "2020+" or a lone year.
    yearRange(title) {
      const t = String(title || '');
      let m = t.match(/\b((?:19|20)\d{2})\s*[-–]\s*((?:19|20)\d{2})\b/);
      if (m) return [Number(m[1]), Number(m[2])];
      m = t.match(/\b((?:19|20)\d{2})\s*\+/);
      if (m) return [Number(m[1]), 9999];
      m = t.match(/\b((?:19|20)\d{2})\b/);
      if (m) return [Number(m[1]), Number(m[1])];
      return null;
    }

    yearsCompatible(a, b) {
      const ra = this.yearRange(a);
      const rb = this.yearRange(b);
      if (!ra || !rb) return true;
      return ra[0] <= rb[1] && rb[0] <= ra[1];
    }

    /* ---------- rendering ---------- */

    container() {
      return document.getElementById('main-cart-items') || document.querySelector('#CartDrawer .drawer__inner');
    }

    removePanel() {
      document.querySelectorAll('.fsn').forEach((el) => el.remove());
    }

    // Keep the panel directly under the drawer header (or at the top of the cart page).
    pin() {
      const panel = this._panel;
      if (!panel) return;
      if (!panel.isConnected) {
        if (this.cart && this.cart.item_count > 0 && !this.busy) this.scheduleCheck();
        return;
      }
      const inner = this.container();
      if (!inner || panel.parentElement !== inner) return;
      const header = inner.querySelector(':scope > .drawer__header');
      const want = header ? header.nextElementSibling : inner.firstElementChild;
      if (want === panel) return;
      if (header) header.insertAdjacentElement('afterend', panel);
      else inner.prepend(panel);
    }

    render(state) {
      const inner = this.container();
      if (!inner) return;
      const cfg = this.cfg;
      const unlocked = !!state.unlocked;

      // This panel owns the perk messaging — drop any notice the promo widget painted.
      if (this.integrated) document.querySelectorAll('.promo-cart-banner, .promo-cart-warning').forEach((el) => el.remove());

      let panel = this._panel && this._panel.isConnected ? this._panel : null;
      if (!panel) {
        this.removePanel();
        panel = document.createElement('div');
        panel.className = 'fsn';
        panel.setAttribute('role', 'status');
        panel.setAttribute('aria-live', 'polite');
        this._panel = panel;
      }
      panel.classList.toggle('fsn--unlocked', unlocked);
      panel.classList.toggle('fsn--tee', !!state.tee);

      const nextIsTee = state.next && state.next.key === 'tee';
      const icon = unlocked ? ICON_CHECK : nextIsTee ? ICON_TEE : ICON_TRUCK;
      panel.classList.toggle('fsn--next-ship', !!state.next && state.next.key === 'ship');
      panel.classList.toggle('fsn--next-tee', !!nextIsTee);

      // One segment per milestone, each filling with its own color toward its own marker.
      const segments = (state.milestones || [])
        .map(
          (m) => `
          <span class="fsn__seg fsn__seg--${m.key}" style="left:${m.from}%;width:${m.at - m.from}%">
            <span class="fsn__seg-fill" style="width:${m.fill}%"></span>
          </span>
          <span class="fsn__marker fsn__marker--${m.key}${m.reached ? ' fsn__marker--reached' : ''}" style="left:${m.at}%"></span>`
        )
        .join('');
      const milestones = (state.milestones || [])
        .map(
          (m) => `
          <span class="fsn__ms fsn__ms--${m.key}${m.reached ? ' fsn__ms--reached' : ''}${state.next && state.next.key === m.key ? ' fsn__ms--next' : ''}">
            <span class="fsn__ms-dot" aria-hidden="true">${m.reached ? ICON_CHECK_SM : ''}</span>
            <span class="fsn__ms-label">${escapeHtml(m.label)}</span>
            <span class="fsn__ms-price">${escapeHtml(m.price)}${m.reached ? '' : '+'}</span>
          </span>`
        )
        .join('');

      let html = `
        <div class="fsn__bar-row">
          <span class="fsn__icon" aria-hidden="true">${icon}</span>
          <div class="fsn__lines">
            <p class="fsn__text">${escapeHtml(state.headline)}</p>
            ${state.sub ? `<p class="fsn__sub">${escapeHtml(state.sub)}</p>` : ''}
          </div>
        </div>
        <div class="fsn__track" aria-hidden="true">${segments}</div>
        ${state.milestones && state.milestones.length > 1 ? `<div class="fsn__milestones">${milestones}</div>` : ''}`;

      // Free tee sitting in the cart at $0 — show it as part of the same block.
      if (state.tee && state.tee.line && state.tee.reached) {
        const l = state.tee.line;
        const size = l.variant_title && l.variant_title !== 'Default Title' ? ` &middot; ${escapeHtml(l.variant_title)}` : '';
        const img = l.image || state.tee.image;
        html += `
          <div class="fsn__tee">
            ${img ? `<img class="fsn__tee-img" src="${escapeHtml(imgSrc(img))}" alt="" width="44" height="44" loading="lazy">` : ''}
            <span class="fsn__tee-text"><strong>${escapeHtml(l.product_title || l.title)}</strong>${size} &middot; ${escapeHtml(this.t('teeInCart'))}</span>
          </div>`;
      }

      // Giveaway entries this order earns (docs/giveaway.md); empty string when no giveaway is running.
      if (window.AxiomGiveaway && typeof window.AxiomGiveaway.cartRowHtml === 'function') {
        html += window.AxiomGiveaway.cartRowHtml(this.cart);
      }

      if (!unlocked && state.suggestions && state.suggestions.length) {
        html += `<p class="fsn__heading">${escapeHtml(cfg.copy.heading)}</p><div class="fsn__cards">`;
        state.suggestions.forEach((s, idx) => {
          html += this.cardHtml(s, idx);
        });
        html += `</div>`;
      }
      panel.innerHTML = html;
      panel._suggestions = state.suggestions || [];
      if (!panel.isConnected) {
        const header = inner.querySelector('.drawer__header');
        if (header && header.parentElement === inner) header.insertAdjacentElement('afterend', panel);
        else inner.prepend(panel);
      }
      this.pin();
      this.bindCards(panel);
    }

    cardHtml(s, idx) {
      const sub = s.subtitle ? `<span class="fsn__card-sub">${escapeHtml(s.subtitle)}</span>` : '';
      const needsOptions = s.kind !== 'another' && s.variantCount > 1;
      const tag = s.inCart ? `<span class="fsn__tag">In your cart &middot; make it 2</span>` : '';
      const cls = ['fsn__card', s.inCart ? 'fsn__card--another' : '', s.closes === false ? 'fsn__card--partial' : ''].join(' ');
      return `
        <div class="${cls.trim()}" data-idx="${idx}">
          <a class="fsn__card-img" href="${escapeHtml(s.url)}" tabindex="-1" aria-hidden="true">
            ${s.image ? `<img src="${escapeHtml(imgSrc(s.image))}" alt="" loading="lazy" width="72" height="72">` : ''}
            ${s.inCart ? '<span class="fsn__x2" aria-hidden="true">&times;2</span>' : ''}
          </a>
          <div class="fsn__card-body">
            ${tag}<a class="fsn__card-title" href="${escapeHtml(s.url)}">${escapeHtml(s.title)}</a>${sub}
            <span class="fsn__card-price">${money(s.price)}</span>
            <p class="fsn__card-reason">${escapeHtml(s.reason)}</p>
            <div class="fsn__card-options"${needsOptions ? '' : ' hidden'}></div>
            <button type="button" class="fsn__add button" data-add="${idx}">
              ${escapeHtml(s.kind === 'another' ? `${this.cfg.copy.add} another` : this.cfg.copy.add)}
            </button>
          </div>
        </div>`;
    }

    bindCards(panel) {
      panel.querySelectorAll('[data-add]').forEach((btn) => {
        btn.addEventListener('click', () => this.onAdd(panel, Number(btn.dataset.add)));
      });
      // Multi-variant products: pull the option list so size/color can be picked inline.
      panel.querySelectorAll('.fsn__card').forEach((card) => {
        const s = panel._suggestions[Number(card.dataset.idx)];
        if (s && s.kind !== 'another' && s.variantCount > 1) this.loadOptions(card, s);
      });
    }

    async loadOptions(card, s) {
      const box = card.querySelector('.fsn__card-options');
      if (!box) return;
      try {
        let product = this.productCache.get(s.handle);
        if (!product) {
          product = await fetch(`/products/${encodeURIComponent(s.handle)}.js`).then((r) => r.json());
          this.productCache.set(s.handle, product);
        }
        if (!card.isConnected) return;
        const options = product.options || [];
        const variants = product.variants || [];
        const firstAvail = variants.find((v) => v.available) || variants[0];
        if (!firstAvail) return;
        box.innerHTML = options
          .map((opt, i) => {
            const name = typeof opt === 'string' ? opt : opt.name;
            const values = [...new Set(variants.map((v) => v[`option${i + 1}`]).filter(Boolean))];
            if (values.length < 2 && options.length === 1) return '';
            return `<label class="fsn__opt"><span>${escapeHtml(name)}</span><select data-opt="${i + 1}">${values
              .map(
                (val) =>
                  `<option value="${escapeHtml(val)}"${val === firstAvail[`option${i + 1}`] ? ' selected' : ''}>${escapeHtml(val)}</option>`
              )
              .join('')}</select></label>`;
          })
          .join('');
        box.hidden = box.innerHTML === '';
        const resolve = () => {
          const picks = [...box.querySelectorAll('select')].map((sel) => sel.value);
          const match = variants.find((v) => picks.every((val, i) => v[`option${i + 1}`] === val));
          s.variantId = match ? match.id : null;
          s.price = match ? match.price : s.price;
          const btn = card.querySelector('[data-add]');
          const priceEl = card.querySelector('.fsn__card-price');
          if (priceEl && match) priceEl.textContent = money(match.price);
          if (btn) {
            btn.disabled = !match || !match.available;
            btn.textContent = !match || match.available ? this.cfg.copy.add : 'Sold out';
          }
        };
        box.querySelectorAll('select').forEach((sel) => sel.addEventListener('change', resolve));
        resolve();
      } catch (e) {
        console.warn('[free-shipping-nudge] options', e);
      }
    }

    async onAdd(panel, idx) {
      const s = panel._suggestions[idx];
      const btn = panel.querySelector(`[data-add="${idx}"]`);
      if (!s || !btn || btn.disabled) return;
      btn.disabled = true;
      const label = btn.textContent;
      btn.textContent = 'Adding…';
      try {
        const sections = this.sectionIds();
        let res;
        if (s.kind === 'another') {
          res = await fetch(routes().cart_change_url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: s.lineKey, quantity: s.quantity + 1, sections, sections_url: location.pathname }),
          });
        } else {
          res = await fetch(routes().cart_add_url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id: s.variantId, quantity: 1 }], sections, sections_url: location.pathname }),
          });
        }
        const data = await res.json();
        if (!res.ok || data.status) throw new Error(data.description || data.message || 'Add failed');
        this.renderCartSections(data.sections);
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: SOURCE, cartData: data });
        }
        this.scheduleCheck();
      } catch (e) {
        console.warn('[free-shipping-nudge] add', e);
        btn.disabled = false;
        btn.textContent = label;
      }
    }

    sectionIds() {
      return document.querySelector('cart-drawer') ? ['cart-drawer', 'cart-icon-bubble'] : ['cart-icon-bubble'];
    }

    // Same section patching as CartDrawer.renderContents, without re-opening the drawer.
    renderCartSections(sections) {
      if (!sections) return;
      const extract = (html, sel) => {
        const el = new DOMParser().parseFromString(html, 'text/html').querySelector(sel);
        return el ? el.innerHTML : null;
      };
      const drawer = document.querySelector('cart-drawer');
      if (drawer && sections['cart-drawer']) {
        const target = document.getElementById('CartDrawer');
        const html = extract(sections['cart-drawer'], '#CartDrawer');
        if (target && html !== null) {
          target.innerHTML = html;
          drawer.classList.remove('is-empty');
          const overlay = drawer.querySelector('#CartDrawer-Overlay');
          if (overlay) overlay.addEventListener('click', drawer.close.bind(drawer));
        }
      }
      const bubble = document.getElementById('cart-icon-bubble');
      if (bubble && sections['cart-icon-bubble']) {
        const html = extract(sections['cart-icon-bubble'], '.shopify-section');
        if (html !== null) bubble.innerHTML = html;
      }
      // On the /cart page Dawn's CartItems handles its own re-render via its own request;
      // a full reload keeps totals honest there.
      if (!drawer && document.getElementById('main-cart-items')) location.reload();
    }
  }

  // Fallback copy (theme-editor settings override these; a setting missing a required
  // [token] falls back here so the sentence still makes sense for both perks).
  const COPY_DEFAULTS = {
    shipLabel: 'FREE shipping',
    teeLabel: 'FREE Dragon Tee',
    shipShort: 'FREE SHIPPING',
    teeShort: 'FREE TEE',
    progress: "You're [amount] away from [perk]",
    unlocked: "You've unlocked [perk]",
    bothUnlocked: "You've unlocked [ship] + a [tee]",
    next: 'Then [amount] more unlocks a [tee].',
    teeWarn: 'Your [tee] is in the cart but not free yet. [amount] more and it drops to $0.00 automatically.',
    teeReady: 'Your [tee] is unlocked. Pick a size when prompted.',
    teeInCart: 'FREE at checkout',
    heading: 'Get there with one of these:',
    pair: 'Bearings wear in pairs. Add the other side and unlock [perk].',
    another: 'Sold per side. Add the other one and unlock [perk].',
    related: 'Fits your truck and unlocks [perk].',
    partial: 'Fits your truck. Brings you within [amount] of [perk].',
    apparel: 'Toss this in and unlock [perk].',
    add: 'Add',
  };

  const ICON_TEE =
    '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M8 3 4 6l2.5 3.5L9 8v12h6V8l2.5 1.5L20 6l-4-3a4 4 0 0 1-8 0z"/></svg>';
  const ICON_CHECK_SM =
    '<svg viewBox="0 0 24 24" width="10" height="10" fill="none" stroke="currentColor" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>';

  // Brand / packaging words that don't describe the part itself.
  const NOISE_WORDS = new Set(
    'axiom supply westin fox bilstein king moog timken skf pair set kit truck trucks'.split(' ')
  );

  const ICON_TRUCK =
    '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M1 3h13v13H1zM14 8h4l3 3v5h-7z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="17.5" cy="18.5" r="2.5"/></svg>';
  const ICON_CHECK =
    '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>';

  customElements.define('free-shipping-nudge', FreeShippingNudge);
})();
